import React, {useState, useEffect} from 'react'
import Plot from 'react-plotly.js'
import {Select} from 'antd'
import axios from 'axios'
import { useSelectedWeek } from '../../../../../../contexts/SelectedWeekContext'

interface DataItem {
  week: string
  value: number
  max: number
  min: number
}
interface Trace {
  x: string[]
  y: number[]
  type: string
  mode: string
  marker?: {color: string}
  line?: {color: string}
  yaxis?: string
  name: string
}
const {Option} = Select

const StreamgraphChart = () => {
  const [data, setData] = useState<DataItem[]>([])
  const {selectedWeek} = useSelectedWeek('2023-W40')
  const [selectedAgeClass, setSelectedAgeClass] = useState<string[]>(['all'])
  const [ageClass, setAgeClass] = useState<string[]>([
    '0_4',
    '5_14',
    '15_34',
    '35_59',
    '60_79',
    '80_plus',
  ])
  const [missingData, setMissingData] = useState<DataItem[]>([])
  const API_URL = process.env.REACT_APP_API_URL
  const [plotData, setPlotData] = useState<Trace[]>([])
  const parseAgeClasses = (selectedAgeClass: string[]): string => {
    return selectedAgeClass.join(',')
  }

  function addMissingWeek(data: DataItem[]): DataItem[] {
    const startWeek = '2023-W34' // First week in the data
    const endWeek = '2024-W18' // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const missingData: DataItem[] = []
    const newData: DataItem[] = []

    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          value: 0,
          max: 0,
          min: 0,
        })
        missingData.push({
          week: currentWeek,
          value: 0,
          max: 0,
          min: 0,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week <= '2023-W52') {
        insertionIndex = i + 1
      } else {
        break
      }
    }
    setMissingData(missingData)
    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }

  const fetchData = async (ageClasses = selectedAgeClass) => {
    try {
      const traces: Trace[] = []
      await Promise.all(
        ageClasses.map(async (ageClass, index) => {
          // Pass index to get color from colors array
          let filterAge = parseAgeClasses([ageClass])
          if (ageClass === 'all') {
            filterAge = parseAgeClasses(['0_4', '5_14', '15_34', '35_59', '60_79', '80_plus'])
          }
          const response = await axios.get(
            API_URL + `/hospitalAdmissionsSari/list?ages=${filterAge}`
          )
          let readableAge
          let selectedColor
          switch (ageClass) {
            case '0_4':
              readableAge = 'less than 5 years'
              selectedColor = '#FF2F00'
              break
            case '5_14':
              readableAge = '5 to 14 years'
              selectedColor = '#33FF57'
              break
            case '15_34':
              readableAge = '15 to 34 years'
              selectedColor = '#5733FF'
              break
            case '35_59':
              readableAge = '35 to 59 years'
              selectedColor = '#FF33DD'
              break
            case '60_79':
              readableAge = '60 to 79 years'
              selectedColor = '#1E778D'
              break
            case '80_plus':
              readableAge = '80 and older'
              selectedColor = '#4B322C'
              break
            default:
              readableAge = 'All ages'
              selectedColor = '#203879'
              break
          }
          const trace: Trace = {
            x: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
              })
              .map((entry) => entry.week),
            y: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
              })
              .map((entry) => entry.value),
            yaxis: 'y',
            type: 'scatter',
            mode: 'lines',
            line: {color: selectedColor}, // Assign color based on index
            name: `incidence of hospitalizations for SARI and Covid-19 - ${readableAge} `, // Add age class to trace name
          }
          traces.push(trace)
        })
      )
      const filterAge = parseAgeClasses(['0_4', '5_14', '15_34', '35_59', '60_79', '80_plus'])
      const response = await axios.get(API_URL + `/hospitalAdmissionsSari/list?ages=${filterAge}`)
      const trace2 = {
        x: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
          })
          .map((entry) => entry.week),
        y: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
          })
          .map((entry) => entry.min),
        type: 'scatter',
        mode: 'lines',
        fill: 'tonexty',
        //yaxis: 'y2',
        marker: {color: '#DBDFE9'},
        name: 'Statistical range of normal percentage*',
      }
      const trace3 = {
        x: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
          })
          .map((entry) => entry.week),
        y: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
          })
          .map((entry) => entry.max),
        type: 'scatter',
        mode: '',
        //yaxis: 'y2',
        marker: {color: '#DBDFE9'},
        name: 'max all ages',
        showlegend: false,
      }
      if (ageClasses.includes('all') && ageClasses.length <= 1) {
        traces.unshift(trace2)
        traces.unshift(trace3)
      }
      const startWeek = '2023-W34' // First week in the data
      const endWeek2 = '2024-W18' // Last week in the data
     const existingWeeks = new Set(response.data.map((item) => item.week)) // Set to store existing weeks
     const missingData: DataItem[] = []
 
     let currentWeek = startWeek
 
     while (currentWeek !== endWeek2) {
       // Move to the next week
       const [year, weekNum] = currentWeek.split('-W')
       const weekNumber = parseInt(weekNum)
       if (weekNumber < 52) {
         currentWeek = `${year}-W${(weekNumber + 1).toString()}`
       } else {
         currentWeek = `${parseInt(year) + 1}-W1`
       }
       if (!existingWeeks.has(currentWeek)) {
   
         missingData.push({
          week: currentWeek,
          value: 0,
          min:0,
          max:0,
         })
       }
     }
     const missdata: Trace = {
      x: missingData
        .filter((entry) => {
          const currentYear = parseInt(entry.week.substring(0, 4), 10)
          const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
          return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
        })
        .map((entry) => entry.week),
      y: missingData
        .filter((entry) => {
          const currentYear = parseInt(entry.week.substring(0, 4), 10)
          const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
          return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
        })
        .map((entry) => entry.value),
      yaxis: 'y',
      type: 'scatter',
      mode: 'markers',
      marker: {color: '#000000'}, // Assign color based on index
      name: `Missing Data `, // Add age class to trace name
    }
    traces.push(missdata)

      setPlotData(traces)
    } catch (error) {
      console.error('Error fetching data:', error)
      setData([])
    }
  }

  useEffect(() => {
    setSelectedAgeClass(selectedAgeClass)
    fetchData(selectedAgeClass)
  }, [selectedAgeClass,selectedWeek])
  const selectedYear = parseInt(selectedWeek.slice(0, 4));
  const selectedWeekNumber = parseInt(selectedWeek.slice(6));
  const startDate = 2023;
  const startWeek = 35;
  const endYear = 2024;
  const endWeek = 18;
  const showArrow = (selectedYear > startDate || (selectedYear === startDate && selectedWeekNumber >= startWeek)) &&
                    (selectedYear < endYear || (selectedYear === endYear && selectedWeekNumber <= endWeek));
  const layout = {
    title:
      'Incidence of hospitalizations of patients with a diagnosis of SARI and patients with a diagnosis of Covid-19 (per 100.000 inhabitants)',
    barmode: 'stack',
    bargap: 0.2,
    bargroupgap: 0.1,
    autosize: true,
    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: -0.035,
        xanchor: 'right',
        y: 0.5,
        yanchor: 'bottom',
        text: '#',
        font: {
          size: 25,
          //color: '#BBBBBC',
        },
        showarrow: false,
      },
      {
        x: selectedWeek,
        y: 0,
        xref: 'x',
        yref: 'y',
        text: showArrow ? 'Selected Week' : '',
        font: {
          family: 'Courier New, monospace',
          size: 16,
          color: '#CC1B1B',
        },
        arrowcolor: '#CC1B1B',
        showarrow: showArrow,
        arrowhead: 7,
        ax: 0,
        ay: -210,
      },
      // {
      //   xref: 'paper',
      //   yref: 'paper',
      //   x: 1.03,
      //   xanchor: 'left',
      //   y: 0.5,
      //   yanchor: 'bottom',
      //   text: '#',
      //   font: {
      //     size: 25,
      //     color: '#BBBBBC',
      //   },
      //   showarrow: false,
      // },
    ],
    xaxis: {
      title: 'Weeks',
      autorange: true,
      type: 'category',
      tickangle: -45,
    },
    yaxis: {
      autorange: true,
    },
    yaxis2: {
      showgrid: false,
      titlefont: {color: 'rgb(148, 103, 189)'},
      tickfont: {color: 'rgb(148, 103, 189)'},
      overlaying: 'y',
      side: 'right',
    },
    legend: {
      x: 0,
      y: -0.6,
      orientation: 'h',
    },
  }

  function handleDataChangeAge(value): void {
    if (value.length === 0) {
      setSelectedAgeClass(['all']);
    } else {
      setSelectedAgeClass(value);
    }  }

  return (
    <>
      <div className='row'>
        <label style={{width: '10%'}} className='col-1 mt-3 text-primary fw-medium fs-5'>
          Age group :
        </label>
        <div className='col-6'>
          <Select
            variant='borderless'
            className='border border-gray-500 border-3 rounded'
            style={{width: '71%'}}
            size='large'
            value={selectedAgeClass}
            onChange={(value) => handleDataChangeAge(value)}
            mode='multiple'
          >
            {ageClass.map((category) => {
              let readableAge
              switch (category) {
                case '0_4':
                  readableAge = 'less than 5 years'
                  break
                case '5_14':
                  readableAge = '5 to 14 years'
                  break
                case '15_34':
                  readableAge = '15 to 34 years'
                  break
                case '35_59':
                  readableAge = '35 to 59 years'
                  break
                case '60_79':
                  readableAge = '60 to 79 years'
                  break
                case '80_plus':
                  readableAge = '80 and older'
                  break
                default:
                  readableAge = 'Unknown'
                  break
              }
              return (
                <Option key={category} value={category}>
                  {readableAge}
                </Option>
              )
            })}
            <Option key={'all'} value={'all'}>
              {'All ages'}
            </Option>
          </Select>
        </div>
      </div>
      <Plot
        style={{width: '100%', height: '500px'}}
        data={plotData}
        layout={layout}
        config={{
          displaylogo: false,
          responsive: true,
        }}
      />
    </>
  )
}

export default StreamgraphChart
